import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { formatApiError } from '@/lib/api';
import { Loader2 } from 'lucide-react';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(username, password);
      navigate('/tasks');
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || 'Login failed');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left: Auth Image - hidden on mobile */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden items-center justify-center bg-zinc-950">
        <img src="https://static.prod-images.emergentagent.com/jobs/89959381-8fb5-45c9-9dbb-0dfb477e8d4d/images/9c9ca0bebd2db0e52e7a7cdf0a1a5f970f505e22cbcc72fb52a8c11be9c6bb5d.png"
          alt="Abstract" className="w-full h-full object-cover opacity-60" />
        <div className="absolute inset-0 flex flex-col items-center justify-center p-12">
          <h1 className="text-5xl font-semibold text-white tracking-tight">Nexus</h1>
          <p className="text-zinc-400 mt-3 text-lg">Your multi-purpose workspace manager</p>
        </div>
      </div>

      {/* Right: Form */}
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm space-y-8">
          <div>
            <h1 className="text-3xl font-semibold tracking-tight lg:hidden">Nexus</h1>
            <h2 className="text-2xl font-medium tracking-tight mt-2">Welcome back</h2>
            <p className="text-sm text-zinc-500 mt-1">Sign in to your account</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 text-sm p-3 rounded-lg" data-testid="login-error">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input id="username" value={username} onChange={e => setUsername(e.target.value)}
                placeholder="Enter username" className="h-12" required data-testid="login-username" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)}
                placeholder="Enter password" className="h-12" required data-testid="login-password" />
            </div>
            <Button type="submit" className="w-full h-12 rounded-xl" disabled={loading} data-testid="login-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Sign In'}
            </Button>
          </form>

          <p className="text-sm text-center text-zinc-500">
            Don't have an account?{' '}
            <Link to="/register" className="text-zinc-900 dark:text-zinc-100 font-medium hover:underline" data-testid="register-link">
              Create one
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
